/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DirectDialFormView.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Form view for creating and editing Direct Dial - simplified using hooks and API modules.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Phone, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import Toggle from '../../components/ui/Toggle';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ModuleDestinationSelect from '../../components/ui/ModuleDestinationSelect';
import { FormBox } from '../../components/ui/FormBox';
import { deleteDirectDialById, navigateToDirectDialDuplicate } from './directDialActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { useDirectDialData } from './hooks/useDirectDialData';
import { useDirectDialForm } from './hooks/useDirectDialForm';
import { fetchDirectDials } from './api/directDialApi';
import { createDefaultFormData } from './utils/formDefaults';
import type { DirectDial } from './types';

export default function DirectDialFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Quick list states
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<DirectDial[]>([]);
  const [initialItems, setInitialItems] = useState<DirectDial[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);

  // Use hooks for data and form management
  const {
    formData,
    setFormData,
    isLoading,
  } = useDirectDialData({
    id,
    duplicateId,
    isEditMode,
  });

  // Store original data for change detection
  const [originalFormData, setOriginalFormData] = useState(createDefaultFormData());

  // Update original data after loading completes (only once)
  useEffect(() => {
    if (isEditMode && !isLoading && formData.code && !originalFormData.code) {
      setOriginalFormData({ ...formData });
    }
  }, [isLoading, isEditMode, formData.code]); // Only depend on code to trigger once after load

  // Use form hook for validation and submission
  const {
    errors,
    setErrors,
    isSaving,
    hasChanges,
    handleSave: handleSaveForm,
    handleCancel: handleCancelForm,
  } = useDirectDialForm({
    formData,
    setFormData,
    originalFormData,
    isEditMode,
    directDialId: id,
  });

  // Field change handler
  const handleChange = useCallback(<K extends keyof typeof formData>(field: K, value: typeof formData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  }, [setFormData, errors, setErrors]);

  // Enhanced save handler
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSaveForm();

    // Update original data after successful save to reset change detection
    if (savedData) {
      const newOriginal = {
        code: savedData.code,
        name: savedData.name,
        description: savedData.description || savedData.name,
        context: savedData.context,
        destinationModule: savedData.destinationModule,
        destinationValue: savedData.destinationValue,
        enabled: savedData.enabled
      };
      setOriginalFormData(newOriginal);
    }

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      return { id, ...formData };
    }

    return savedData;
  }, [handleSaveForm, formData, isEditMode, id]);

  // Use reusable hook for save actions
  const mode = isEditMode ? 'edit' : 'create';
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/applications/direct-dial',
    newPath: '/pbx/applications/direct-dial/new',
    mode,
    getEditPath: (savedData) => `/pbx/applications/direct-dial/${savedData.uuid || savedData.id}`,
    getFormData: () => formData,
    storageKey: 'directDial:saveAndNewData',
  });

  // Navigate to list
  const handleGoToList = () => {
    navigate('/pbx/applications/direct-dial');
  };

  // Navigate to new
  const handleAddNew = () => {
    navigate('/pbx/applications/direct-dial/new');
  };

  // Delete handlers
  const handleDeleteClick = () => {
    if (!isEditMode || !id) return;
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteDirectDialById(id);
      showSuccess(t('directDial.messages.deletedSuccess'));
      navigate('/pbx/applications/direct-dial');
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('directDial.messages.deleteError');
      showError(msg);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  // Duplicate handler
  const handleDuplicateFromForm = () => {
    if (!isEditMode || !id) return;
    navigateToDirectDialDuplicate(navigate, id);
  };

  // Quick list handlers
  const loadInitialItems = useCallback(async () => {
    if (initialItems.length > 0) return;

    setIsLoadingInitial(true);
    try {
      const result = await fetchDirectDials({
        page: 1,
        limit: 100,
        sortBy: 'code',
        sortOrder: 'asc',
      });
      setInitialItems(result.data);
      setQuickListItems(result.data);
    } catch (error) {
      setInitialItems([]);
      setQuickListItems([]);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [initialItems.length]);

  const loadQuickList = useCallback(async (search: string) => {
    const normalizedSearch = search.trim();
    if (!normalizedSearch || normalizedSearch.length < 2) {
      setQuickListItems(initialItems);
      return;
    }

    setIsSearching(true);
    try {
      const result = await fetchDirectDials({
        page: 1,
        limit: 50,
        sortBy: 'code',
        sortOrder: 'asc',
        search: normalizedSearch,
      });
      setQuickListItems(result.data);
    } catch (error) {
      setQuickListItems([]);
    } finally {
      setIsSearching(false);
    }
  }, [initialItems]);

  // Load initial items when modal opens
  useEffect(() => {
    if (showQuickList && initialItems.length === 0) {
      loadInitialItems();
    }
  }, [showQuickList, initialItems.length, loadInitialItems]);

  // Debounced search for quick list
  useEffect(() => {
    if (!showQuickList) {
      setQuickListSearch('');
      return;
    }

    const timer = setTimeout(() => {
      if (quickListSearch.trim().length >= 2) {
        loadQuickList(quickListSearch);
      } else {
        setQuickListItems(initialItems);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [quickListSearch, showQuickList, loadQuickList, initialItems]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-20">
      <ModuleFormHeader
        icon={<Phone className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('directDial.editTitle')} - ${formData.code || ''}` : t('directDial.createTitle')}
        backButtonText={t('directDial.actions.list')}
        onBack={handleGoToList}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('directDial.actions.quickList'),
            onClick: () => setShowQuickList(true)
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('directDial.actions.duplicate'),
              onClick: handleDuplicateFromForm
            }]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('directDial.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="mt-8">
        <form onSubmit={(e) => { e.preventDefault(); handleSaveForm(); }}>
          <FormBox
            title={t('directDial.basicInformation')}
            icon={<Phone className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
              <Label htmlFor="code">
                <SimpleTooltip content={t('directDial.fields.code.tooltip')}>
                  <span>
                    {t('directDial.fields.code.label')}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </SimpleTooltip>
              </Label>
              <div>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => handleChange('code', e.target.value)}
                  placeholder={t('directDial.fields.code.placeholder')}
                  error={errors.code}
                  disabled={isEditMode}
                />
              </div>

              <Label htmlFor="name">
                <SimpleTooltip content={t('directDial.fields.name.tooltip')}>
                  <span>
                    {t('directDial.fields.name.label')}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </SimpleTooltip>
              </Label>
              <div>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder={t('directDial.fields.name.placeholder')}
                  error={errors.name}
                />
              </div>

              <Label>
                <SimpleTooltip content={t('directDial.fields.enabled.tooltip')}>
                  <span>{t('directDial.fields.enabled.label')}</span>
                </SimpleTooltip>
              </Label>
              <div>
                <Toggle
                  value={formData.enabled}
                  onChange={(checked) => handleChange('enabled', checked)}
                />
              </div>
              <div className="col-span-2"></div>

              <div className="col-span-4 border-t border-border my-2"></div>

              <Label htmlFor="destinationValue">
                <SimpleTooltip content={t('directDial.fields.destinationValue.tooltip')}>
                  <span>
                    {t('directDial.fields.destinationValue.label')}
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </SimpleTooltip>
              </Label>
              <div className="col-span-3">
                <ModuleDestinationSelect
                  moduleValue={formData.destinationModule || ''}
                  destinationValue={formData.destinationValue || ''}
                  onModuleChange={(value) => handleChange('destinationModule', value || '')}
                  onDestinationChange={(value) => handleChange('destinationValue', value || '')}
                  modulePlaceholder={t('directDial.fields.destinationModule.placeholder')}
                  destinationPlaceholder={t('directDial.fields.destinationValue.placeholder')}
                />
                {errors.destinationValue && (
                  <p className="text-sm text-red-500 mt-1">{errors.destinationValue}</p>
                )}
              </div>
            </div>
          </FormBox>
        </form>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancelForm}
        onDelete={isEditMode ? handleDeleteClick : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        deleteDisabled={isDeleting}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="directDial:saveAction"
        saveDisabled={isSaving}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('directDial.deleteTitle')}
        message={t('directDial.messages.confirmDelete', { name: formData.name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('directDial.quickList.title')}
        items={quickListItems.filter(i => i.id != null)}
        isLoadingInitial={isLoadingInitial}
        isSearching={isSearching}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/applications/direct-dial/${item.uuid || item.id}`);
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            onClick={() => {
              if (!isCurrent) {
                setShowQuickList(false);
                setQuickListSearch('');
                navigate(`/pbx/applications/direct-dial/${item.uuid || item.id}`);
              }
            }}
            disabled={isCurrent}
            className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
              }`}
          >
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-foreground">
                    {item.code}
                  </span>
                  {isCurrent && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                      {t('directDial.current')}
                    </span>
                  )}
                </div>
                {item.name && (
                  <div className="text-sm text-muted-foreground truncate">
                    {item.name}
                  </div>
                )}
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('directDial.quickList.searchPlaceholder')}
        minCharsMessage={t('directDial.quickList.minChars')}
        emptyMessage={t('directDial.quickList.empty')}
        noResultsMessage={t('directDial.quickList.noResults')}
        headerIcon={<Phone className="h-5 w-5" />}
        minSearchChars={2}
      />
    </div>
  );
}
